import React, { useState, useEffect } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as yup from "yup";
import ImportStudentsModal from "../../modals/ImportStudentsModal";
import { PREDEFINED_SCALES } from "../../pages/teacher/GradeScalesManager";
import { getGrades } from "../../../utils/grades";
import useUserStore from "../../../stores/user";
import { useUiLang } from "../../../stores/uiLang";
import { CLASS_FORM_T, termOptions, termLabel } from "../../../utils/classForm";

interface ManageClassProps {
  cls: {
    id: string;
    name: string;
    description?: string;
    semester: string;
    settings: {
      class_time: string;
      reflection_requirements?: {
        min_words?: number;
        min_sentences?: number;
        enabled: boolean;
      };
    };
    grade_type: "ABC" | "1234" | "%";
    join_code?: string | null;
  };
  onBack: () => void;
  onUpdate: (data: {
    name: string;
    description?: string;
    semester: string;
    settings: {
      class_time: string;
      reflection_requirements?: {
        min_words?: number;
        min_sentences?: number;
        enabled: boolean;
      };
    };
    grade_type: string;
    createJoinCode: boolean;
  }) => Promise<{ error: string | null }>;
}

const validationSchema = yup.object().shape({
  name: yup.string().required("Class name is required"),
  reflection_min_words: yup
    .number()
    .min(1, "Minimum words must be at least 1")
    .nullable()
    .when("reflection_requirements_enabled", {
      is: true,
      then: (schema) =>
        schema.required("Minimum words is required when reflection is enabled"),
    }),
  reflection_min_sentences: yup
    .number()
    .min(1, "Minimum sentences must be at least 1")
    .nullable()
    .when("reflection_requirements_enabled", {
      is: true,
      then: (schema) =>
        schema.required(
          "Minimum sentences is required when reflection is enabled"
        ),
    }),
});

export default function ManageClass({
  cls,
  onBack,
  onUpdate,
}: ManageClassProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const lang = useUiLang();
  const t = CLASS_FORM_T[lang];
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [importResults, setImportResults] = useState<{
    message?: string;
    importedEmails: string[];
    newImportedStudents: {
      firstName: string;
      lastName: string;
      email: string;
      temporaryPassword: string;
    }[];
    failedEmails: { email: string; reason: string }[];
    error?: string;
  } | null>(null);

  const { user } = useUserStore();
  const [customScales, setCustomScales] = useState<any[]>([]);

  useEffect(() => {
    if (user?.id) {
      getGrades(user.id).then(({ grades }) => setCustomScales(grades));
    }
  }, [user?.id]);

  // Build grade scale options
  const gradeScaleOptions = [
    ...PREDEFINED_SCALES.map((scale) => ({
      id: scale.id,
      name: scale.name,
      isPredefined: true,
    })),
    ...customScales.map((scale) => ({
      id: scale.id,
      name: scale.name,
      isPredefined: false,
    })),
  ];

  const initialGradeType = (() => {
    if (cls.grade_type) return cls.grade_type;
    if (gradeScaleOptions.length > 0) return gradeScaleOptions[0].name;
    return "";
  })();

  const initialValues = {
    name: cls.name,
    description: cls.description || "",
    semester: cls.semester,
    class_time: cls.settings.class_time || "",
    grade_type: initialGradeType,
    createJoinCode: !cls.join_code,
    reflection_requirements_enabled:
      false,
    reflection_min_words: cls.settings.reflection_requirements?.min_words || 75,
    reflection_min_sentences:
      cls.settings.reflection_requirements?.min_sentences || 2,
  };

  function automaticallyDownloadStudentsInfo(importResults: any) {
    const csvContent = [
      ["First Name", "Last Name", "Email", "Temporary Password"],
      ...importResults.newImportedStudents.map((student) => [
        student.firstName,
        student.lastName,
        student.email,
        student.temporaryPassword,
      ]),
    ]
      .map((row) => row.join(","))
      .join("\n");

    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "new_students.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  return (
    <div>
      {/* Header Actions */}
      <div className="flex justify-between items-center mb-5">
        <h2 className="text-lg font-medium text-gray-900">Manage Class</h2>
        <div className="flex space-x-3">
          <button
            onClick={() => setIsImportModalOpen(true)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="-ml-1 mr-2 h-5 w-5 text-gray-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
              />
            </svg>
            Import Students
          </button>
          <button
            onClick={onBack}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            Back to Classes
          </button>
        </div>
      </div>

      {importResults && (
        <div className="mb-4 p-4 bg-white border border-gray-200 rounded shadow relative">
          <button
            className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
            onClick={() => setImportResults(null)}
          >
            ✕
          </button>
          <h4 className="text-sm font-medium text-gray-900 mb-2">
            Import Summary
          </h4>

          {importResults.error ? (
            <p className="text-sm text-red-600">{importResults.error}</p>
          ) : (
            <>
              {importResults.message && (
                <p className="text-sm text-green-700 mb-2">
                  {importResults.message}
                </p>
              )}
              {importResults.importedEmails.length > 0 && (
                <div className="mb-2">
                  <p className="text-sm text-green-700">✅ Imported:</p>
                  <ul className="list-disc ml-6 text-xs text-green-800">
                    {importResults.importedEmails.map((email, i) => (
                      <li key={`${i}-${email}`}>{email}</li>
                    ))}
                  </ul>
                </div>
              )}
              {importResults.newImportedStudents.length > 0 && (
                <div className="mb-2">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-blue-700">
                      🆕 {importResults.newImportedStudents.length} New Students
                      Created
                    </p>
                    <button
                      onClick={() => {
                        const csvContent = [
                          [
                            "First Name",
                            "Last Name",
                            "Email",
                            "Temporary Password",
                          ],
                          ...importResults.newImportedStudents.map(
                            (student) => [
                              student.firstName,
                              student.lastName,
                              student.email,
                              student.temporaryPassword,
                            ]
                          ),
                        ]
                          .map((row) => row.join(","))
                          .join("\n");

                        const blob = new Blob([csvContent], {
                          type: "text/csv;charset=utf-8;",
                        });
                        const link = document.createElement("a");
                        const url = URL.createObjectURL(blob);
                        link.setAttribute("href", url);
                        link.setAttribute("download", "new_students.csv");
                        link.style.visibility = "hidden";
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                      }}
                      className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="-ml-1 mr-1.5 h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                        />
                      </svg>
                      Download New Students Info
                    </button>
                  </div>
                  <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded-md">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg
                          className="h-5 w-5 text-yellow-400"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-yellow-700">
                          Please download this information now. For security
                          reasons, the temporary passwords will not be
                          accessible later.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {importResults.failedEmails.length > 0 && (
                <div>
                  <p className="text-sm text-yellow-700">⚠️ Failed:</p>
                  <ul className="list-disc ml-6 text-xs text-yellow-800">
                    {importResults.failedEmails.map(({ email, reason }, i) => (
                      <li key={`${i}-${email}`}>
                        {email} — {reason}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      )}

      <div className="bg-white shadow rounded-lg border border-gray-200">
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={async (values, { resetForm }) => {
            try {
              setIsSubmitting(true);
              const result = await onUpdate({
                name: values.name,
                description: values.description,
                semester: values.semester,
                settings: {
                  class_time: values.class_time,
                  // CT-6: the reflection word/sentence minimums are no longer offered.
                  reflection_requirements: {
                    enabled: false,
                  },
                },
                grade_type: values.grade_type,
                createJoinCode: values.createJoinCode,
              });

              if (!result.error) {
                resetForm({ values });
              }
            } finally {
              setIsSubmitting(false);
            }
          }}
        >
          {({ values }) => (
            <Form className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">
                  {t.className}
                </label>
                <Field
                  name="name"
                  className="w-full border p-2 rounded"
                  placeholder={t.classNamePlaceholder}
                />
                <ErrorMessage
                  name="name"
                  component="div"
                  className="text-sm text-red-600 mt-1"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    {t.classTime}
                  </label>
                  <Field
                    name="class_time"
                    placeholder={t.classTimePlaceholder}
                    className="w-full border p-2 rounded"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">
                    {t.term}
                  </label>
                  <Field
                    as="select"
                    name="semester"
                    className="w-full border p-2 rounded"
                  >
                    {/* Run E2 J2 (D31): terms worked out from today's date */}
                    {termOptions(values.semester).map((term) => (
                      <option key={term} value={term}>
                        {termLabel(term, lang)}
                      </option>
                    ))}
                  </Field>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  {t.gradeType}
                </label>
                <Field
                  as="select"
                  name="grade_type"
                  className="w-full border p-2 rounded"
                >
                  {gradeScaleOptions.map((scale) => (
                    <option key={scale.id} value={scale.name}>
                      {scale.name}
                    </option>
                  ))}
                </Field>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  {t.description}
                </label>
                <Field
                  as="textarea"
                  name="description"
                  rows={3}
                  placeholder={t.descriptionPlaceholder}
                  className="w-full border p-2 rounded"
                />
              </div>

              {!cls.join_code && (
                <div className="flex items-center space-x-2">
                  <Field
                    type="checkbox"
                    name="createJoinCode"
                    className="h-4 w-4"
                  />
                  <label className="text-sm">{t.joinCode}</label>
                </div>
              )}
              <div className="flex justify-end space-x-2 pt-4 border-t">
                <button
                  type="button"
                  onClick={onBack}
                  disabled={isSubmitting}
                  className={`px-4 py-2 border rounded text-sm text-gray-700 bg-white ${
                    isSubmitting
                      ? "bg-gray-300 cursor-not-allowed"
                      : "bg-white hover:bg-gray-50"
                  }`}
                >
                  {t.cancel}
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`px-4 py-2 text-sm rounded text-white ${
                    isSubmitting
                      ? "bg-indigo-300 cursor-not-allowed"
                      : "bg-indigo-600 hover:bg-indigo-700"
                  }`}
                >
                  {isSubmitting ? t.saving : t.save}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>

      <ImportStudentsModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        onComplete={(results) => {
          setImportResults(results);
          if (results.newImportedStudents.length > 0) {
            automaticallyDownloadStudentsInfo(results);
          }
        }}
        selectedClassId={cls.id}
      />
    </div>
  );
}
